import type { UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import type { FamilyMemberDetections } from 'src/data-capture/hooks/file-uploads/useFamilyFileDetections';
import type { MeetingScope } from 'src/meetings/scope';

// The family every client attendee belongs to, or undefined as soon as one of them belongs to a
// different family or to none. Read from the attendees' own client records: the adviser's families
// list holds only the households assigned to them, so a colleague's would look like no family at
// all and its members be treated as unrelated clients.
export function sharedFamilyId(participantFamilyIds: (string | undefined)[]): string | undefined {
  const [first] = participantFamilyIds;
  if (!first) return undefined;
  return participantFamilyIds.every(familyId => familyId === first) ? first : undefined;
}

// A recording's file is owned by a single client, so only the family endpoints can read and
// finalise the detections of the other clients in the meeting. Showing the suggestions and
// finalising a review must use the same family, or a finalised review never clears its suggestion.
// A family member who didn't attend can still have detections on the recording, so with
// `reviewsWholeFamily` a lone attendee's family is read too.
//
// `familyId` is the family the meeting is reviewed through. Reading it from the lead attendee puts
// the whole meeting under their family and silently drops everyone outside it, which is only
// acceptable while there is no per-client copy for those clients to be reviewed through instead.
export function resolveRecordingFamilyId(
  scope: MeetingScope,
  clientParticipantCount: number,
  familyId: string | undefined,
  reviewsWholeFamily = false,
): string | undefined {
  if (scope.kind === 'family') return scope.familyId;
  return clientParticipantCount > 1 || reviewsWholeFamily ? familyId : undefined;
}

type ProfileUpdateTarget = { id: string; firstName: string; name: string };
type NamedClient = { id: string; firstName: string; lastName: string };

// Attendees keep their meeting details. Anyone else with data to review is named from
// `detectedClients`: a family member who didn't attend, or — when the meeting spans families, so
// there is no family review to list them — a non-attendee with a row on the recording itself.
// Dropping them here would leave data detected for them that no card can ever reach.
export function profileUpdateTargets(
  clientIds: string[],
  participants: ProfileUpdateTarget[],
  detectedClients: NamedClient[],
): ProfileUpdateTarget[] {
  const participantById = new Map(participants.map(participant => [participant.id, participant]));
  const detectedById = new Map(detectedClients.map(client => [client.id, client]));
  return clientIds.flatMap(id => {
    const participant = participantById.get(id);
    if (participant) return [participant];
    const detected = detectedById.get(id);
    if (!detected) return [];
    return [
      {
        id,
        firstName: detected.firstName,
        name: `${detected.firstName} ${detected.lastName}`.trim(),
      },
    ];
  });
}

export function clientsAwaitingProfileUpdate({
  recordingFamilyId,
  recordingFile,
  familyDetectionMembers,
  canReviewEveryDetectedClient = false,
}: {
  recordingFamilyId: string | undefined;
  recordingFile: UploadedFile | undefined;
  familyDetectionMembers: FamilyMemberDetections[];
  canReviewEveryDetectedClient?: boolean;
}): string[] {
  if (!recordingFile) return [];
  // A family review resolves the file only once every member is done, so the file settles them all.
  if (recordingFamilyId) {
    if (recordingFile.isResolved) return [];
    return familyDetectionMembers
      .filter(member => member.detections.length > 0 && !member.isProcessed)
      .map(member => member.client.id);
  }
  // Each client outside a family reviews their own copy of the recording, so everyone detected in
  // it can be offered a card. Without that copy the client file endpoints serve the file only to
  // the client who owns it, and anyone else's review would 404.
  //
  // Resolving the file is how every review entry point outside the family flow marks it done, but
  // the file belongs to the owner alone: it settles the owner's card whatever their detection row
  // says, and leaves everyone else to be settled by their own row, which their copy latches when
  // they finalise. Letting it settle everyone would retract the other clients' cards the moment
  // the owner finished, before they had reviewed anything.
  return recordingFile.detectedClients
    .filter(detected => {
      if (detected.isProcessed) return false;
      if (detected.id === recordingFile.clientId) return !recordingFile.isResolved;
      return canReviewEveryDetectedClient;
    })
    .map(detected => detected.id);
}
